import { A } from "@solidjs/router";
import Text from "./Text";
import { Show, onMount, onCleanup } from "solid-js";
import { useUserContext } from "./UserContextProvider";
import { useRoleParser } from "../hooks/permissions";
import { useTranslation } from "../hooks/useTranslation";
import { useMobileSidebar } from "../hooks/useMobileSidebar";
import { useSidebarCollapse } from "../hooks/useSidebarCollapse";
import FeatureGate from "./FeatureGate";
import LicenseGate from "./LicenseGate";
import { GrantTypes } from "../services/grantsApi";
import { useMessageUnread } from "../state/messageUnread";

interface DashboardNavbarProps {
    children: any;
}

export function DashboardNavbar({ children }: DashboardNavbarProps) {

    const { user, userTenant } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const { t } = useTranslation();
    const { sidebarOpen, closeSidebar } = useMobileSidebar();
    const { collapsed, toggleCollapse } = useSidebarCollapse();
    const unread = useMessageUnread();

    const isAdmin = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf("Admin")];
    const isOAuth = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf("OAuth")];
    const isRoot = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf("Root")];

    const nav = () => {
        if (window.innerWidth < 768) closeSidebar();
    };

    let sidebarRef: HTMLDivElement | undefined;
    const SIDEBAR_SCROLL_KEY = 'dashboard-sidebar-scroll';
    const saveSidebarScroll = () => {
        if (sidebarRef) sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarRef.scrollTop));
    };
    onMount(() => {
        if (!sidebarRef) return;
        const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
        if (saved) sidebarRef.scrollTop = parseInt(saved, 10) || 0;
        sidebarRef.addEventListener('scroll', saveSidebarScroll, { passive: true });
        onCleanup(() => sidebarRef?.removeEventListener('scroll', saveSidebarScroll));
    });

    return (
        <div class="dashboard-container">
            <Show when={sidebarOpen()}>
                <div class="sidebar-backdrop" onClick={closeSidebar} />
            </Show>
            <div ref={sidebarRef} class={"dashboard-sidebar" + (sidebarOpen() ? " sidebar-open" : "") + (collapsed() ? " sidebar-collapsed" : "")}>

                <span class="sidebar-section-label">{t('SIDEBAR_BOTS')}</span>

                <A activeClass="active" inactiveClass="inactive" href="/bots" onClick={nav}>
                    <i class="fa-solid fa-id-card"></i><Text val="BOT_REGISTRY_NAV" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/botengine" end onClick={nav}>
                    <i class="fa-solid fa-server"></i><Text val="BOTENGINE" />
                </A>
                <FeatureGate flag="MULTI_ORCHESTRATE">
                    <A activeClass="active" inactiveClass="inactive" href="/multi" onClick={nav}>
                        <i class="fa-solid fa-tower-broadcast"></i><Text val="MULTI_NAV" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="SCHEMATICS">
                    <A activeClass="active" inactiveClass="inactive" href="/schematics" onClick={nav}>
                        <i class="fa-solid fa-cubes"></i><Text val="SCHEMATICS_NAV" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="MAPART">
                    <A activeClass="active" inactiveClass="inactive" href="/mapart" onClick={nav}>
                        <i class="fa-solid fa-map"></i><Text val="SIDEBAR_MAPART" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="MAP_CACHE_PAGE">
                    <A activeClass="active" inactiveClass="inactive" href="/map-cache" onClick={nav}>
                        <i class="fa-solid fa-images"></i><Text val="SIDEBAR_MAP_CACHE" />
                    </A>
                </FeatureGate>
                <LicenseGate type={GrantTypes.LicenseShopStock}>
                    <A activeClass="active" inactiveClass="inactive" href="/shopstock" onClick={nav}>
                        <i class="fa-solid fa-boxes-stacked"></i>ShopStock
                    </A>
                    <FeatureGate flag="SHOP_STOCK_WATCHLIST">
                        <A activeClass="active" inactiveClass="inactive" href="/shopstock/watchlist" onClick={nav}>
                            <i class="fa-solid fa-eye"></i><Text val="SHOP_STOCK_WATCH_TITLE" />
                        </A>
                    </FeatureGate>
                </LicenseGate>
                <FeatureGate flag="VOTING">
                    <A activeClass="active" inactiveClass="inactive" href="/voting" onClick={nav}>
                        <i class="fa-solid fa-square-poll-vertical"></i><Text val="VOTING_TITLE" />
                    </A>
                </FeatureGate>

                <span class="sidebar-section-label">{t('SIDEBAR_PLUGINS')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/botplugins" onClick={nav}>
                    <i class="fa-solid fa-puzzle-piece"></i><Text val="BOT_PLUGINS" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/plugins/overview" onClick={nav}>
                    <i class="fa-solid fa-table-cells-large"></i><Text val="PLUGIN_OVERVIEW_NAV" />
                </A>
                <LicenseGate type={GrantTypes.SellbotSeat}>
                    <A activeClass="active" inactiveClass="inactive" href="/sellbot" end onClick={nav}>
                        <i class="fa-solid fa-store"></i><Text val="SELLBOT_NAV" />
                    </A>
                </LicenseGate>
                <FeatureGate flag="PAYMENTS">
                    <A activeClass="active" inactiveClass="inactive" href="/payments" onClick={nav}>
                        <i class="fa-solid fa-money-bill-transfer"></i><Text val="PAYMENTS_NAV" />
                    </A>
                </FeatureGate>
                <LicenseGate type={GrantTypes.PlotAclSeat}>
                    <A activeClass="active" inactiveClass="inactive" href="/plotacl" end onClick={nav}>
                        <i class="fa-solid fa-shield-halved"></i><Text val="PLOTACL_NAV" />
                    </A>
                </LicenseGate>
                <FeatureGate flag="PLAYER_LIST">
                    <A activeClass="active" inactiveClass="inactive" href="/playerlists" end onClick={nav}>
                        <i class="fa-solid fa-users-rectangle"></i><Text val="PLAYERLIST_NAV" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="AUTOMATION">
                    <A activeClass="active" inactiveClass="inactive" href="/bot/automation" end onClick={nav}>
                        <i class="fa-solid fa-bolt"></i><Text val="AUTOMATION_NAV" />
                    </A>
                </FeatureGate>

                <span class="sidebar-section-label">{t('SIDEBAR_STORAGE')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/inventory" end onClick={nav}>
                    <i class="fa-solid fa-cube"></i><Text val="INVENTORY" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/inventory/storages" onClick={nav}>
                    <i class="fa-solid fa-warehouse"></i><Text val="STORAGES" />
                </A>
                <FeatureGate flag="CRAFT_ORDERS">
                    <A activeClass="active" inactiveClass="inactive" href="/inventory/craft/orders" onClick={nav}>
                        <i class="fa-solid fa-list-check"></i><Text val="INVENTORY_CRAFT_ORDER_TITLE" />
                    </A>
                    <FeatureGate flag="STOCK_PLANNING">
                        <A activeClass="active" inactiveClass="inactive" href="/inventory/demand" onClick={nav}>
                            <i class="fa-solid fa-truck-ramp-box"></i><Text val="INVENTORY_DEMAND_TITLE" />
                        </A>
                    </FeatureGate>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/inventory/audit" onClick={nav}>
                    <i class="fa-solid fa-clipboard-list"></i><Text val="INVENTORY_AUDIT_LOG" />
                </A>

                <span class="sidebar-section-label">{t('SIDEBAR_MONITORING')}</span>
                <Show when={(isAdmin() || isRoot()) && user().showAdminUiInMemberApp}>
                    <FeatureGate flag="DETAILED_MONITORING">
                        <A activeClass="active" inactiveClass="inactive" href="/disconnects/bots" onClick={nav}>
                            <i class="fa-solid fa-plug-circle-xmark"></i><Text val="BOT_DISCONNECTS_BOT_TITLE" />
                        </A>
                        <A activeClass="active" inactiveClass="inactive" href="/disconnects/possessions" onClick={nav}>
                            <i class="fa-solid fa-ghost"></i><Text val="BOT_DISCONNECTS_POSSESSION_TITLE" />
                        </A>
                    </FeatureGate>
                </Show>
                <A activeClass="active" inactiveClass="inactive" href="/online-players" onClick={nav}>
                    <i class="fa-solid fa-users"></i><Text val="ONLINE_PLAYERS_NAV" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/username-tracker" onClick={nav}>
                    <i class="fa-solid fa-user-clock"></i><Text val="USERNAME_TRACKER_NAV" />
                </A>
                <FeatureGate flag="SEED_CRACKER_PAGE">
                    <A activeClass="active" inactiveClass="inactive" href="/seed-cracker" onClick={nav}>
                        <i class="fa-solid fa-seedling"></i><Text val="SEED_CRACK_NAV" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="AH_HISTORY">
                    <A activeClass="active" inactiveClass="inactive" href="/ah" onClick={nav}>
                        <i class="fa-solid fa-gavel"></i><Text val="AH_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/ah/watches" onClick={nav}>
                        <i class="fa-solid fa-bell"></i><Text val="AH_NAV_WATCHES" />
                    </A>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/chatlogs" onClick={nav}>
                    <i class="fa-solid fa-comment"></i><Text val="CHATLOGS" />
                </A>
                <FeatureGate flag="MSGS">
                    <A activeClass="active" inactiveClass="inactive" href="/messages" onClick={nav}>
                        <i class="fa-solid fa-comments"></i><Text val="NAV_MSGS" />
                        <Show when={unread.unreadCount() > 0}>
                            <span style={{
                                "margin-left": "auto",
                                "min-width": "1.25rem",
                                height: "1.25rem",
                                padding: "0 var(--spacing-xs)",
                                "border-radius": "var(--radius-sm)",
                                background: "var(--error-500)",
                                color: "white",
                                "font-size": "var(--font-size-xs)",
                                "font-weight": "var(--font-weight-semibold)",
                                display: "inline-flex",
                                "align-items": "center",
                                "justify-content": "center",
                                "line-height": "1",
                            }}>{unread.unreadCount()}</span>
                        </Show>
                    </A>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/auditlogs" onClick={nav}>
                    <i class="fa-solid fa-bolt"></i><Text val="AUDITLOGS" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/activity" onClick={nav}>
                    <i class="fa-solid fa-stream"></i><Text val="ACTIVITY_FEED_NAV" />
                </A>

                <span class="sidebar-section-label">{t('SIDEBAR_LICENSING')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/mylicenses" onClick={nav}>
                    <i class="fa-solid fa-id-badge"></i><Text val="MY_LICENSES_NAV" />
                </A>

                <Show when={isOAuth()}>
                    <span class="sidebar-section-label">{t('SIDEBAR_OAUTH')}</span>
                    <A activeClass="active" inactiveClass="inactive" href="/oauthclients" onClick={nav}>
                        <i class="fa-solid fa-globe"></i><Text val="MYOAUTHCLIENTS" />
                    </A>
                </Show>

                <Show when={user()?.loggedin && userTenant()?.tenantId}>
                    <span class="sidebar-section-label">{t('SIDEBAR_TEAM')}</span>
                    <A activeClass="active" inactiveClass="inactive" href="/tenant/settings" onClick={nav}>
                        <i class="fa-solid fa-sliders"></i><Text val="TENANT_SETTINGS" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/tenant/members" onClick={nav}>
                        <i class="fa-solid fa-users"></i><Text val="TENANT_MEMBERS" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/tenant/grants" onClick={nav}>
                        <i class="fa-solid fa-coins"></i><Text val="TENANT_GRANTS_NAV" />
                    </A>
                </Show>

                <FeatureGate flag="REMOTEX_LEGACY">
                    <span class="sidebar-section-label">{t('SIDEBAR_LEGACY')}</span>
                    <A activeClass="active" inactiveClass="inactive" href="/dashboard" onClick={nav}>
                        <i class="fa-solid fa-gauge"></i><Text val="DASHBOARD" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/mybots" onClick={nav}>
                        <i class="fa-solid fa-robot"></i><Text val="MYBOTS" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/mycommands" onClick={nav}>
                        <i class="fa-solid fa-person-running"></i><Text val="MYCOMMANDS" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/mygrants" onClick={nav}>
                        <i class="fa-solid fa-share-nodes"></i><Text val="MYGRANTS" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/dropconfigs" onClick={nav}>
                        <i class="fa-sharp fa-solid fa-box-archive"></i><Text val="DROP_CONFIGS" />
                    </A>
                </FeatureGate>

                <span class="sidebar-section-label">{t('SIDEBAR_SETTINGS')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/settings" onClick={nav}>
                    <i class="fa-solid fa-gear"></i><Text val="SETTINGS" />
                </A>
                <FeatureGate flag="API_KEYS">
                    <A activeClass="active" inactiveClass="inactive" href="/apikeys" onClick={nav}>
                        <i class="fa-solid fa-key"></i><Text val="SIDEBAR_API_KEYS" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="AUTOMATION">
                    <A activeClass="active" inactiveClass="inactive" href="/webhooks" onClick={nav}>
                        <i class="fa-solid fa-satellite-dish"></i><Text val="SIDEBAR_WEBHOOKS" />
                    </A>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/me" onClick={nav}>
                    <i class="fa-solid fa-user"></i>{user()?.username}
                </A>

                <button
                    class="sidebar-collapse-toggle"
                    onClick={toggleCollapse}
                    title={collapsed() ? t('SIDEBAR_EXPAND') : t('SIDEBAR_COLLAPSE')}
                    aria-label={collapsed() ? t('SIDEBAR_EXPAND') : t('SIDEBAR_COLLAPSE')}
                >
                    <i class={`fa-solid ${collapsed() ? "fa-angles-right" : "fa-angles-left"}`} />
                    <span class="sidebar-collapse-label"><Text val="SIDEBAR_COLLAPSE_LABEL" /></span>
                </button>

            </div>
            <div class="dashboard-content">
                {children}
            </div>
        </div>
    );

}